import { Phone } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import {
  Combobox,
  ComboboxDescription,
  ComboboxLabel,
  ComboboxOption,
} from '@/components/ui/catalyst/combobox';
import type { PhoneOption } from '@/types/provider-destinations';

// ---------------------------------------------------------------------------
// Source label mapping
// ---------------------------------------------------------------------------

const SOURCE_LABELS: Record<string, string> = {
  parallel_enrichment: 'Parallel',
  voiceai: 'VoiceAI',
  manual: 'Manual',
  google_places: 'Google',
  upwork_escalation: 'Upwork',
  system: 'System',
  directory_create: 'Directory',
  intake_conversion: 'Intake',
  artifact_extraction: 'Extraction',
  treatment_check_in: 'Check-in',
  api_create: 'API',
  request_template: 'Template',
};

const PURPOSE_LABELS: Record<string, string> = {
  GENERAL: 'General',
  RECORDS: 'Records',
  BILLING: 'Billing',
  SCHEDULING: 'Scheduling',
  EMERGENCY: 'Emergency',
  CLINICAL: 'Clinical',
};

// ---------------------------------------------------------------------------
// Sentinel for "custom number" option
// ---------------------------------------------------------------------------

const CUSTOM_OPTION_ID = '__custom__';

interface PhoneOrCustom {
  kind: 'phone';
  phone: PhoneOption;
}

interface CustomEntry {
  kind: 'custom';
  id: typeof CUSTOM_OPTION_ID;
}

type SelectableOption = PhoneOrCustom | CustomEntry;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FollowUpPhoneSelectProps {
  value: string;
  onChange: (phone: string) => void;
  availablePhones: PhoneOption[];
  placeholder?: string;
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FollowUpPhoneSelect = ({
  value,
  onChange,
  availablePhones,
  placeholder = 'Select or enter phone number',
  className,
}: FollowUpPhoneSelectProps) => {
  const [isCustomMode, setIsCustomMode] = useState(false);

  // Build the options list: phones + "Enter custom number" sentinel
  const options = useMemo<SelectableOption[]>(() => {
    const phoneOptions: SelectableOption[] = availablePhones.map((phone) => ({
      kind: 'phone' as const,
      phone,
    }));
    const customOption: SelectableOption = {
      kind: 'custom' as const,
      id: CUSTOM_OPTION_ID,
    };
    return [...phoneOptions, customOption];
  }, [availablePhones]);

  const displayValue = useCallback(
    (option: SelectableOption | null): string | undefined => {
      if (!option) return value || undefined;
      if (option.kind === 'custom') return 'Enter custom number...';
      return option.phone.value;
    },
    [value],
  );

  const filterOption = useCallback(
    (option: SelectableOption, query: string): boolean => {
      if (option.kind === 'custom') return true;
      const { phone } = option;
      const q = query.toLowerCase();
      return (
        phone.value.toLowerCase().includes(q) ||
        (phone.label?.toLowerCase().includes(q) ?? false) ||
        (phone.purpose?.toLowerCase().includes(q) ?? false)
      );
    },
    [],
  );

  // Headless UI Combobox interprets `undefined` as uncontrolled mode.
  // Ideally we'd use `null` for "no selection" to stay controlled, but
  // the HUI type signature only allows `T | undefined`. In practice this
  // is safe because the Combobox only renders when availablePhones.length > 0,
  // and `value` will almost always match one of the options (it's set from
  // voiceai_followup_phone which is derived from the same phone list).
  const selectedOption = useMemo<SelectableOption | undefined>(() => {
    if (!value) return undefined;
    return options.find(
      (o) => o.kind === 'phone' && o.phone.value === value,
    );
  }, [value, options]);

  const handleChange = useCallback(
    (option: SelectableOption | null) => {
      if (!option) {
        onChange('');
        return;
      }
      if (option.kind === 'custom') {
        setIsCustomMode(true);
        onChange('');
        return;
      }
      setIsCustomMode(false);
      onChange(option.phone.value);
    },
    [onChange],
  );

  // When the combobox popover closes, if the user typed something that doesn't
  // match any known phone, accept it as a custom value instead of reverting.
  const handleCloseWithQuery = useCallback(
    (query: string) => {
      if (!query) return;
      if (!availablePhones.some((p) => p.value === query)) {
        setIsCustomMode(true);
        onChange(query);
      }
    },
    [availablePhones, onChange],
  );

  // When in custom mode, render a plain input
  if (isCustomMode || availablePhones.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2">
          <input
            type="tel"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter phone number"
            className="relative block w-full appearance-none rounded-lg border border-zinc-950/10 bg-transparent py-1.5 pr-3 pl-3 text-sm/6 text-zinc-950 placeholder:text-zinc-500 focus:outline-hidden data-hover:border-zinc-950/20"
          />
          {availablePhones.length > 0 && (
            <button
              type="button"
              onClick={() => setIsCustomMode(false)}
              className="shrink-0 rounded border border-zinc-200 px-2 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
            >
              Browse
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <Combobox
        value={selectedOption}
        onChange={handleChange}
        options={options}
        displayValue={displayValue}
        filter={filterOption}
        placeholder={placeholder}
        onCloseWithQuery={handleCloseWithQuery}
      >
        {(option: SelectableOption) => {
          if (option.kind === 'custom') {
            return (
              <ComboboxOption value={option}>
                <ComboboxLabel>
                  <span className="text-blue-600">+ Enter custom number</span>
                </ComboboxLabel>
              </ComboboxOption>
            );
          }

          const { phone } = option;
          const purposeLabel = phone.purpose
            ? PURPOSE_LABELS[phone.purpose] ?? phone.purpose
            : null;
          const sourceLabel = phone.source
            ? SOURCE_LABELS[phone.source] ?? phone.source
            : null;

          return (
            <ComboboxOption value={option}>
              <Phone className="size-4 shrink-0 text-zinc-500" data-slot="icon" />
              <ComboboxLabel>
                <span className="font-medium">{phone.value}</span>
                {phone.label && (
                  <span className="ml-1.5 text-zinc-500">
                    {phone.label}
                  </span>
                )}
              </ComboboxLabel>
              <ComboboxDescription>
                {purposeLabel && (
                  <span className="rounded bg-zinc-100 px-1 py-0.5 text-xs font-medium text-zinc-600">
                    {purposeLabel}
                  </span>
                )}
                {sourceLabel && (
                  <span className="ml-1 text-xs text-zinc-500">
                    via {sourceLabel}
                  </span>
                )}
                {phone.is_preferred && (
                  <span className="ml-1 rounded bg-blue-50 px-1 py-0.5 text-xs font-medium text-blue-600">
                    Preferred
                  </span>
                )}
              </ComboboxDescription>
            </ComboboxOption>
          );
        }}
      </Combobox>
    </div>
  );
};
